import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { paymentTypeLabel } from '@supply-mover-2-0/core';
import type { PaymentReceiptLineModel } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { DetailPageSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { saveOrShareBlob } from '../lib/downloadFile';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalTransactionService from '../services/portalTransactionService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * "Visa ending 4242" / "Debit Card ending 0008" / "ACH" — the receipt's own
 * wording. PD-16381: an unnamed card is named by its funding ("Card" when
 * unclassified), never by the stored 'Credit Card' type.
 */
function methodLabel(method: {
	type: string;
	lastFour: string;
	name: string;
	cardCategory?: string | null;
}): string {
	const base =
		method.name !== ''
			? method.name
			: paymentTypeLabel(method.type, method.cardCategory);
	if (base === '') {
		return '';
	}
	return method.lastFour !== '' ? `${base} ending ${method.lastFour}` : base;
}

/** A credit memo reduces what was charged, however the row is signed. */
function signedApplied(line: PaymentReceiptLineModel): number {
	return line.kind === 'credit_memo' ? -Math.abs(line.applied) : line.applied;
}

/**
 * Transaction detail (PD-15870) — one payment and the documents it covered,
 * with a Download Receipt button that streams the receipt PDF. Mirrors the
 * statement detail's mobile-first layout (cards, not tables).
 *
 * The data is the RECEIPT model, already scoped by the server: a child
 * billed through a parent sees only its own lines, labelled as its portion,
 * and never the parent's card or the whole charge.
 */
export function TransactionDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [isDownloading, setIsDownloading] = useState(false);

	const transactionId = id != null ? parseInt(id, 10) : NaN;

	const { data, isLoading } = useQuery({
		queryKey: ['portal-transaction', transactionId],
		queryFn: () =>
			portalTransactionService.getTransactionDetail(token ?? '', transactionId),
		enabled: token != null && !Number.isNaN(transactionId),
		staleTime: 60_000,
	});

	const record = data?.data ?? null;
	const receipt = record?.receipt ?? null;
	const portion = receipt?.portion ?? null;
	const receiptAvailable = record?.receiptAvailable === true;
	const reference = String(
		receipt?.transaction.summaryId ?? record?.id ?? transactionId,
	);

	const handleDownload = useCallback(async () => {
		if (record == null || token == null) {
			return;
		}
		setIsDownloading(true);
		try {
			const blob = await portalTransactionService.downloadReceiptPdf(
				token,
				record.id,
			);
			// Browser: normal download. Native shell: write to cache +
			// share sheet — the anchor click silently no-ops in the
			// WebView (PD-14606).
			await saveOrShareBlob(blob, `receipt-${reference}.pdf`);
		} catch (err) {
			console.error('Error downloading receipt PDF:', err);
		} finally {
			setIsDownloading(false);
		}
	}, [record, token, reference]);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	const status = record?.displayStatus ?? record?.mPaymentStatus ?? '';
	const method = receipt != null ? methodLabel(receipt.transaction.method) : '';

	const meta: Array<{ label: string; value: string }> =
		record != null && receipt != null
			? [
					{
						label: 'Payment Date',
						value: formatStatementDate(receipt.transaction.date),
					},
					{ label: 'Status', value: status !== '' ? status : '-' },
					...(portion != null
						? [
								{
									label: 'Portion for {{account}}',
									value: formatCurrency(portion.amount) ?? '-',
								},
								{ label: 'Paid by', value: portion.paidByAccountName },
							]
						: [
								{
									label: 'Total Charged',
									value: formatCurrency(receipt.totalCharged) ?? '-',
								},
								...(method !== ''
									? [{ label: 'Paid With', value: method }]
									: []),
							]),
					...(receipt.cycleDate != null && receipt.cycleDate !== ''
						? [
								{
									label: 'Cycle',
									value: formatStatementDate(receipt.cycleDate),
								},
							]
						: []),
				]
			: [];

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<MobilePageHeader
				title={
					record != null ? `${t('Transaction')} ${reference}` : 'Transaction'
				}
				backPath="/transactions"
			/>
			<div className="flex items-center justify-between mb-4">
				<h1 className="hidden md:block text-lg md:text-xl font-bold text-default">
					{record != null
						? `${t('Transaction')} ${t(reference)}`
						: t('Transaction')}
				</h1>
				{record != null && receiptAvailable && (
					<button
						type="button"
						onClick={() => {
							void handleDownload();
						}}
						disabled={isDownloading}
						className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
					>
						<FontAwesomeIcon icon={faDownload} className="mr-2" />
						{isDownloading ? t('Preparing...') : t('Download Receipt')}
					</button>
				)}
			</div>

			{isLoading ? (
				/* Skeleton shaped like the statement detail (PD-14692) */
				<DetailPageSkeleton />
			) : record == null || receipt == null ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					{t('Transaction not found')}
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{/* Account */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
						<div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
							{t('Account')}
						</div>
						<div className="text-sm font-medium text-default">
							{t(receipt.account.name)}
						</div>
						{/* PD-15870 — the parent's card paid; this page shows only
						    this account's share of that payment. */}
						{portion != null && (
							<p className="text-xs text-brand-muted mt-1">
								{t(
									'This payment was made by {{account}}. Only the documents for your account are shown, with their total as your portion.',
									{ account: t(portion.paidByAccountName) },
								)}
							</p>
						)}
					</div>

					{/* Meta */}
					<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
						{meta.map((cell) => (
							<div
								key={cell.label}
								className="bg-brand-surface border border-brand-border rounded-xl p-3"
							>
								<div className="text-xs text-brand-muted">
									{t(cell.label, {
										account: t(portion?.accountName ?? ''),
									})}
								</div>
								<div className="text-sm font-semibold text-default">
									{t(cell.value)}
								</div>
							</div>
						))}
					</div>

					{/* Documents the payment covered */}
					<div>
						<div className="font-semibold text-default mb-2">
							{t('Documents Paid')}
						</div>
						{receipt.lines.length === 0 ? (
							<div className="bg-brand-surface border border-brand-border rounded-xl p-4 text-sm text-brand-muted">
								{(record.relatedOrderIds ?? []).length > 0
									? t('This payment was applied to an order.')
									: t('No documents were settled by this payment.')}
							</div>
						) : (
							<div className="flex flex-col gap-2">
								{receipt.groups.map((group, groupIndex) => (
									<div
										key={group.accountName ?? `own-${groupIndex}`}
										className="flex flex-col gap-2"
									>
										{/* A parent's charge settles its children's invoices —
										    say which store each block belongs to. */}
										{group.accountName != null && (
											<div className="text-xs uppercase tracking-wide text-brand-muted mt-2">
												{t('Billed for {{account}}', {
													account: t(group.accountName),
												})}
											</div>
										)}
										{group.lines.map((line) => (
											<button
												key={line.invoiceId}
												type="button"
												onClick={() => {
													void navigate(`/invoices/${line.invoiceId}`);
												}}
												className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:border-portal-accent"
											>
												<div className="min-w-0">
													<div className="text-sm font-medium text-default">
														{t(
															line.kind === 'credit_memo'
																? 'Credit Memo'
																: 'Invoice',
														)}{' '}
														#{t(line.number)}
													</div>
													<div className="text-xs text-brand-muted">
														{line.date !== ''
															? formatStatementDate(line.date)
															: t('-')}
													</div>
												</div>
												<div className="text-right shrink-0">
													<div
														className={`text-sm font-semibold ${
															line.kind === 'credit_memo'
																? 'text-success'
																: 'text-default'
														}`}
													>
														{formatCurrency(signedApplied(line))}
													</div>
													<div className="text-xs text-brand-muted">
														{t('Applied')}
													</div>
												</div>
											</button>
										))}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Totals — the full receipt's summary lines; a scoped receipt
					    carries none of them (they describe the payer's charge). */}
					{portion == null && (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3 flex flex-col gap-1 text-sm">
							{receipt.fundsApplied > 0 && (
								<div className="flex items-center justify-between text-brand-muted">
									<span>{t('Account funds applied')}</span>
									<span>-{formatCurrency(receipt.fundsApplied)}</span>
								</div>
							)}
							{receipt.fee > 0 && (
								<div className="flex items-center justify-between text-brand-muted">
									<span>{t('Processing fee included')}</span>
									<span>{formatCurrency(receipt.fee)}</span>
								</div>
							)}
							<div className="flex items-center justify-between font-semibold text-default">
								<span>{t('Total charged')}</span>
								<span>{formatCurrency(receipt.totalCharged)}</span>
							</div>
						</div>
					)}
					{portion != null && (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-center justify-between text-sm font-semibold text-default">
							<span>
								{t('Portion for {{account}}', {
									account: t(portion.accountName),
								})}
							</span>
							<span>{formatCurrency(portion.amount)}</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
